import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { logEvent } from '@/lib/analytics/firebase';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getMatchOutcomePick,
  placeMatchOutcomePick,
  type MatchOutcomeSide,
} from '@/lib/data/provider';

/**
 * The 1X2 coins-game state for a fixture: the per-side multipliers plus the
 * caller's own pick. Keyed by user so a sign-in/out swaps the cached pick.
 */
export function useMatchOutcomePick(fixtureId: string | undefined, enabled = true) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['outcome-pick', fixtureId ?? 'none', userId ?? 'anon'],
    queryFn: () => getMatchOutcomePick(fixtureId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && enabled && Boolean(fixtureId),
  });
}

/**
 * Stake coins on a match outcome. On success we refresh this fixture's pick
 * (so the card flips to its locked state) AND the ['rewards'] wallet family,
 * so the user's coin balance reflects the just-spent stake everywhere.
 */
export function usePlaceMatchOutcomePick(fixtureId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pick: MatchOutcomeSide; stake: number }) =>
      placeMatchOutcomePick(fixtureId, input.pick, input.stake),
    onSuccess: (_row, input) => {
      logEvent('outcome_pick_placed', {
        fixture_id: fixtureId,
        pick: input.pick,
        stake: input.stake,
      });
      queryClient.invalidateQueries({ queryKey: ['outcome-pick', fixtureId] });
      // The stake is debited server-side; refresh the coin wallet (and the rest
      // of the rewards family) so the balance updates immediately.
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
    },
  });
}
